import { useEffect, useRef } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Etiqueta from "../kit/Etiqueta";
import EstadoChip from "../kit/EstadoChip";
import EstrellaFavorito from "./EstrellaFavorito";
import { tokens } from "../../theme";
import { abrirEnOtraPestana, pideOtraPestana } from "../../utils/enlaces";
import { TemaApp } from "../../types/tema";
import { categoriaDe, coguiaDe, tiposDeTema } from "../../utils/categoria";
import { useFocoLista } from "../../utils/estadoLista";
import { formatFecha } from "../../utils/fecha";
import { chipDeTema, temaRevocadoParaMi } from "../../utils/estados";

/**
 * Catálogo de temas como lista compacta. Cada tema es una fila clicable con:
 *   Título
 *   [Estado] [Categoría] [Tipos…]
 *   Guía · Coguía (si tiene)
 * Extensible a cualquier usuario: se usa también para los temas propios cuando
 * la persona tiene más de uno.
 *
 * Al abrir un tema se anota cuál fue: si se vuelve a este listado, esa fila
 * queda destacada y a la vista, para retomar la lectura donde se dejó.
 *
 * `conFavorito` pinta la estrella. Va en el catálogo, que es donde uno recorre
 * temas ajenos y quiere apartar alguno; en "Mis temas" no: el tema propio no se
 * guarda para después, ya es tuyo.
 */
export default function TemaLista({ temas, conFavorito = false }: { temas: TemaApp[]; conFavorito?: boolean }) {
  const { foco, marcarFoco } = useFocoLista();

  return (
    <Stack spacing={1}>
      {temas.map((tema) => (
        <ItemTema key={tema.id} tema={tema} destacado={tema.id === foco} onAbrir={marcarFoco} conFavorito={conFavorito} />
      ))}
    </Stack>
  );
}

function ItemTema({
  tema,
  destacado,
  onAbrir,
  conFavorito,
}: {
  tema: TemaApp;
  destacado: boolean;
  onAbrir: (id: number) => void;
  conFavorito: boolean;
}) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const tipos = tiposDeTema(tema);
  const chip = chipDeTema(tema);
  // Revocado: la fila se queda —hay que poder leer qué tema era— pero inerte. Sale
  // también del recorrido con teclado: no lleva a ninguna acción.
  const revocado = temaRevocadoParaMi(tema);

  // Al volver de la ficha: el tema del que se viene se centra y toma el foco,
  // así el teclado sigue desde ahí y no desde el principio de la página.
  useEffect(() => {
    if (!destacado) return;
    ref.current?.scrollIntoView({ block: "center" });
    ref.current?.focus({ preventScroll: true });
  }, [destacado]);

  // Con Ctrl/⌘ (o el botón central) se abre en otra pestaña y esta lista se
  // queda donde está: es lo que hace un enlace de verdad, y comparar varios temas
  // es justamente lo que uno viene a hacer al catálogo.
  const abrir = (e?: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean; button?: number }) => {
    if (e && pideOtraPestana(e)) {
      abrirEnOtraPestana(`/app/temas/${tema.id}`);
      return;
    }
    onAbrir(tema.id);
    navigate(`/app/temas/${tema.id}`);
  };

  return (
    <Paper
      ref={ref}
      elevation={0}
      role="button"
      tabIndex={revocado ? -1 : 0}
      aria-disabled={revocado || undefined}
      onClick={revocado ? undefined : abrir}
      onKeyDown={(e) => {
        if (!revocado && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          abrir(e);
        }
      }}
      sx={{
        border: "1px solid",
        borderColor: destacado ? tokens.azul[400] : "divider",
        borderRadius: "8px",
        padding: "11px 14px",
        cursor: revocado ? "default" : "pointer",
        opacity: revocado ? 0.55 : 1,
        outline: "none",
        backgroundColor: destacado ? tokens.tabla.filaHover : undefined,
        // Anillo azul de 2 px (kit): el mismo para el foco y para la fila de la
        // que se viene, que es justamente dónde quedó el foco.
        boxShadow: destacado ? `0 0 0 2px ${tokens.azul[200]}` : "none",
        "&:hover": revocado ? undefined : { borderColor: tokens.azul[200] },
        "&:focus-visible": revocado ? undefined : { borderColor: tokens.azul[400], boxShadow: `0 0 0 2px ${tokens.azul[200]}` },
      }}
    >
      {/* La estrella va arriba a la derecha, alineada con el título: es lo único
          clicable dentro de una fila que ya es un botón entero. */}
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 0.75 }}>
        <Typography
          sx={{
            fontFamily: (t) => t.typography.h6.fontFamily,
            fontWeight: 600,
            fontSize: 15,
            lineHeight: 1.3,
            color: tokens.tabla.celdaFuerte,
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          {tema.titulo}
        </Typography>
        {conFavorito && <EstrellaFavorito temaId={tema.id} favorito={tema.es_favorito} tamano={18} />}
      </Stack>

      {/* Estado (primero) + categoría + tipos de trabajo. */}
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.6} sx={{ mb: 0.75 }}>
        <EstadoChip label={chip.label} tono={chip.tono} icono={chip.icono} denso />
        <Etiqueta>{categoriaDe(tema)}</Etiqueta>
        {tema.areas.length > 1 && <Etiqueta variante="contorno">+{tema.areas.length - 1}</Etiqueta>}
        {tipos.map((tipo) => (
          <Etiqueta key={tipo} variante="contorno">
            {tipo}
          </Etiqueta>
        ))}
      </Stack>

      <GuiaCoguia tema={tema} />
    </Paper>
  );
}

/** Guía, coguía y fecha de publicación en una sola línea tenue, para ocupar poco
 * espacio. Compartida por la lista y la tarjeta de "Mi tema" para que se vean igual. */
export function GuiaCoguia({ tema }: { tema: TemaApp }) {
  const coguia = coguiaDe(tema);
  return (
    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
      Guía: <Dato>{tema.guia}</Dato>
      {coguia && (
        <>
          {"  ·  "}
          {coguia.label}: <Dato>{coguia.value}</Dato>
        </>
      )}
      {tema.fecha_creacion && (
        <>
          {"  ·  "}
          Creado: <Dato>{formatFecha(tema.fecha_creacion)}</Dato>
        </>
      )}
    </Typography>
  );
}

function Dato({ children }: { children: string | null }) {
  return (
    <Box component="span" sx={{ color: children ? "text.primary" : "text.disabled", fontWeight: 500 }}>
      {children || "—"}
    </Box>
  );
}
